import { useEffect, useState, useCallback } from "react";
import { briefingApi } from "@/lib/api-client";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { fmtUSDFull } from "@/lib/format";
import { Sunrise, ShieldAlert, Bell, Sparkles, X, ChevronRight } from "lucide-react";

const PRIORITY_COLOR = { critical: "#EF4444", high: "#F59E0B", medium: "#3B82F6", low: "#71717A" };

export default function CommandBriefing({ userId }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);

  const showBriefingOnce = useCallback(async () => {
    if (!userId) return;
    const key = `uc_briefing_${userId}_${new Date().toDateString()}`;
    if (sessionStorage.getItem(key)) return;
    try {
      const d = await briefingApi.get();
      setData(d);
      setOpen(true);
      sessionStorage.setItem(key, "1");
    } catch (e) { console.error("briefing load failed", e); }
  }, [userId]);

  useEffect(() => { showBriefingOnce(); }, [showBriefingOnce]);

  if (!data) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="bg-[#0A0A0A] border border-amber-500/30 text-white max-w-2xl p-0 rounded-none"
        data-testid="command-briefing"
        aria-describedby={undefined}
      >
        <div className="p-6 border-b border-white/10 relative overflow-hidden"
             style={{ background: "linear-gradient(135deg, rgba(245,158,11,0.08), transparent)" }}>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 flex items-center justify-center border border-amber-500/40 bg-black">
              <Sunrise size={16} className="text-amber-400"/>
            </div>
            <div>
              <div className="uc-chip"><span className="uc-dot bg-amber-400 uc-pulsing"></span> COMMAND BRIEFING</div>
              <div className="font-mono-uc text-[10px] uppercase tracking-wider text-white/40 mt-1">
                {new Date(data.date).toLocaleString(undefined, { weekday: "long", month: "long", day: "numeric" })}
              </div>
            </div>
          </div>
          <DialogHeader>
            <DialogTitle className="font-serif-display text-3xl text-white leading-tight">{data.greeting}</DialogTitle>
          </DialogHeader>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard label="Net Worth" value={fmtUSDFull(data.stats.net_worth)} />
            <StatCard label="Ventures" value={data.stats.ventures} />
            <StatCard label="Red Docs" value={data.stats.red_flagged_docs} accent={data.stats.red_flagged_docs > 0 ? "#EF4444" : undefined} />
            <StatCard label="Alerts" value={data.stats.unread_alerts} accent={data.stats.unread_alerts > 0 ? "#F59E0B" : undefined} />
          </div>

          <div>
            <div className="flex items-center gap-2 mb-3">
              <Sparkles size={13} className="text-amber-400"/>
              <span className="font-mono-uc text-[11px] uppercase tracking-widest text-white/70">Top 3 Critical Tasks</span>
            </div>
            <div className="space-y-2">
              {data.top_tasks.length === 0 ? (
                <div className="p-4 border border-white/10 bg-black text-center text-white/50 text-sm italic">
                  No critical tasks pending. Deploy an agent to surface new priorities.
                </div>
              ) : data.top_tasks.map((t, i) => (
                <div key={t.id} className="p-3 border border-white/10 flex items-center gap-3 hover:border-white/25 transition-colors" data-testid={`briefing-task-${i}`}>
                  <div className="font-serif-display text-2xl text-amber-400 w-8">{i + 1}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white/90">{t.title}</div>
                    <div className="font-mono-uc text-[10px] text-white/50 uppercase tracking-wider mt-0.5">
                      {t.source_agent || "system"} · due {new Date(t.due_date).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="font-mono-uc text-[9px] uppercase tracking-wider px-2 py-1 border" style={{ borderColor: PRIORITY_COLOR[t.priority], color: PRIORITY_COLOR[t.priority] }}>
                    {t.priority}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <Button data-testid="briefing-dismiss" onClick={() => setOpen(false)} className="uc-btn-amber rounded-none w-full h-11">
            Begin Operations <ChevronRight size={14} className="ml-2"/>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className="p-3 border border-white/10 bg-black">
      <div className="font-mono-uc text-[10px] uppercase tracking-wider text-white/40 mb-1">{label}</div>
      <div className="font-serif-display text-xl" style={{ color: accent || "#FFFFFF" }}>{value}</div>
    </div>
  );
}
